import { IProcedureLogs, IToothDetails } from '@dataTypes/appointment.type'
import { IPhaseItems } from '@dataTypes/treatmentPlan.types'
import {
  getTSFromToothSurfaces,
  parseSurfaceString,
} from '@utils/treatment-plan.utils'

export type ToothSurfaceEntry = {
  area_type: number | null
  area?: string
  tooth?: string
  surf?: string
}

export const getToothSurfaceEntriesFromPhaseItem = (
  item: IPhaseItems,
): ToothSurfaceEntry[] => {
  const fromToothSurfaces = getTSFromToothSurfaces(item.tooth_surfaces)
  if (fromToothSurfaces.length > 0) {
    return fromToothSurfaces
  }
  return parseSurfaceString(item.surface)
}

export const phaseItemHasToothOnlySurfaces = (item: IPhaseItems): boolean => {
  const entries = getToothSurfaceEntriesFromPhaseItem(item)
  if (entries.length === 0) {
    return false
  }
  return entries.every(
    (entry) => entry.area_type === 1 && Boolean(entry.tooth?.trim()),
  )
}

const sameProcedureIdentity = (a: IPhaseItems, b: IPhaseItems): boolean => {
  if (a.procedure_id && b.procedure_id) {
    return a.procedure_id === b.procedure_id
  }
  const aCode = a.procedure_settings?.procedure?.procedure_code
  const bCode = b.procedure_settings?.procedure?.procedure_code
  return Boolean(aCode && bCode && aCode === bCode)
}

/** Consecutive tooth-only rows from the same multi-tooth picker session. */
export const collectPhaseToothSurfaceSiblings = (
  items: IPhaseItems[],
  targetIndex: number,
): IPhaseItems[] => {
  const target = items[targetIndex]
  if (!target || !phaseItemHasToothOnlySurfaces(target)) {
    return target ? [target] : []
  }
  let start = targetIndex
  while (
    start > 0 &&
    sameProcedureIdentity(items[start - 1], target) &&
    phaseItemHasToothOnlySurfaces(items[start - 1])
  ) {
    start -= 1
  }
  let end = targetIndex
  while (
    end < items.length - 1 &&
    sameProcedureIdentity(items[end + 1], target) &&
    phaseItemHasToothOnlySurfaces(items[end + 1])
  ) {
    end += 1
  }
  return items.slice(start, end + 1)
}

export const mergePhaseItemsToToothSurfaceEntries = (
  items: IPhaseItems[],
): ToothSurfaceEntry[] => {
  return items.flatMap((item) => getToothSurfaceEntriesFromPhaseItem(item))
}

export const getAppointmentToothDetailEntries = (
  procedure: Partial<IProcedureLogs>,
): ToothSurfaceEntry[] => {
  const entries = procedure.tooth_details ?? []
  if (entries.length === 0) {
    return []
  }
  return entries.map((entry) => ({
    area_type: entry.area_type ?? null,
    area: entry.area ?? undefined,
    tooth: entry.tooth ?? undefined,
    surf: entry.surf ?? undefined,
  }))
}

export const appointmentRowHasToothOnlySurfaces = (
  procedure: Partial<IProcedureLogs>,
): boolean => {
  const entries = getAppointmentToothDetailEntries(procedure)
  if (entries.length === 0) {
    return false
  }
  return entries.every(
    (entry) => entry.area_type === 1 && Boolean(entry.tooth?.trim()),
  )
}

const sameAppointmentProcedureIdentity = (
  a: Partial<IProcedureLogs>,
  b: Partial<IProcedureLogs>,
): boolean => {
  if (a.proc_id && b.proc_id) {
    return a.proc_id === b.proc_id
  }
  return Boolean(
    a.proc_code &&
      b.proc_code &&
      a.proc_code === b.proc_code &&
      a.proc_description === b.proc_description,
  )
}

export const collectAppointmentToothSurfaceSiblingIndices = (
  procedures: Partial<IProcedureLogs>[],
  targetIndex: number,
): number[] => {
  const target = procedures[targetIndex]
  if (!target || !appointmentRowHasToothOnlySurfaces(target)) {
    return targetIndex >= 0 ? [targetIndex] : []
  }
  let start = targetIndex
  while (
    start > 0 &&
    sameAppointmentProcedureIdentity(procedures[start - 1], target) &&
    appointmentRowHasToothOnlySurfaces(procedures[start - 1])
  ) {
    start -= 1
  }
  let end = targetIndex
  while (
    end < procedures.length - 1 &&
    sameAppointmentProcedureIdentity(procedures[end + 1], target) &&
    appointmentRowHasToothOnlySurfaces(procedures[end + 1])
  ) {
    end += 1
  }
  return Array.from({ length: end - start + 1 }, (_, offset) => start + offset)
}

export const mergeAppointmentRowsToToothSurfaceEntries = (
  procedures: Partial<IProcedureLogs>[],
  indices: number[],
): ToothSurfaceEntry[] => {
  return indices.flatMap((index) =>
    getAppointmentToothDetailEntries(procedures[index] ?? {}),
  )
}

export const toothDataToAppointmentLogEntries = (
  toothData: IToothDetails[],
): Partial<IProcedureLogs>[] => {
  const entries: Partial<IProcedureLogs>[] = []
  for (const toothDetail of toothData) {
    const areaType = toothDetail.area_type ?? 1
    if (areaType !== 1) {
      entries.push({
        area_type: areaType,
        area: toothDetail.area ?? null,
        tooth: null,
        surf: null,
      })
      continue
    }
    if (toothDetail.surf?.length) {
      for (const surface of toothDetail.surf) {
        entries.push({
          area_type: areaType,
          tooth: toothDetail.tooth_num?.toString() ?? null,
          surf: surface,
        })
      }
      continue
    }
    if (toothDetail.tooth_num != null) {
      entries.push({
        area_type: areaType,
        tooth: toothDetail.tooth_num.toString(),
        surf: null,
      })
    }
  }
  return entries
}
